import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormikContext } from 'formik'
import React from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import NoContent from 'src/views/components/NoContent'
import { PaginationBottom, PaginationTop } from 'src/views/components/Pagination'
import { SortButtons } from 'src/views/components/SortButtons'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

// Constants
const DEFAULT_PAGE = '1'
const DEFAULT_PER_PAGE = '10'
const DEFAULT_STATUS = '1'

interface FormikValues {
  readonly name: string
  readonly status: string
}

export default function AdministratorFacultiesPage(): JSX.Element {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('faculty:faculties')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()
  const [searchParams, setSearchParams] = useSearchParams({
    page: DEFAULT_PAGE,
    perPage: DEFAULT_PER_PAGE,
    name: '',
    status: DEFAULT_STATUS,
    sortBy: '',
  })

  const name = searchParams.get('name') || ''
  const status = searchParams.get('status') || ''
  const initialValues: FormikValues = { name, status }

  const breadcrumbsItems = [
    {
      page: t('faculty:faculties'),
      path: `/${locale}/administrator/administration/faculties`,
    },
  ]

  const handleFormSubmit = React.useCallback(
    (values: FormikValues) => {
      setSearchParams((params) => {
        Object.entries(values).forEach(([key, value]) => {
          if (value) {
            params.set(key, value)
          } else {
            params.delete(key)
          }
        })
        params.set('page', DEFAULT_PAGE) // Reset to first page on filter change
        return params
      })
    },
    [setSearchParams]
  )

  const navigate = useNavigate()
  const canManageFaculties = useAdminAccess().canManage('faculties')

  return (
    <Section
      title={t('faculty:faculties')}
      rightElement={
        canManageFaculties ? (
          <Button variant="blue" onClick={() => navigate('/administrator/administration/faculties/add')}>
            <FontAwesomeIcon icon={ProRegularSvgIcons.faPlus} /> {t('faculty:add_faculty')}
          </Button>
        ) : undefined
      }
      breadcrubms={breadcrumbsItems}
      icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faUniversity} />}
    >
      <Form
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        classNames={{
          form: 'w-full px-2 mt-3 mb-1',
        }}
        enableReinitialize
      >
        <FormSearch />
      </Form>

      <SuspenseWrapper>
        <TableContent searchParams={searchParams} />
      </SuspenseWrapper>
    </Section>
  )
}

function FormSearch(): React.ReactElement | null {
  const formik = useFormikContext<FormikValues>()
  const t = useTranslatable()
  const [searchParams, setSearchParams] = useSearchParams()

  const currentName = searchParams.get('name') || ''
  const currentStatus = searchParams.get('status') || ''

  const statusOptions = React.useMemo(
    () => [
      { value: '', label: t('common:any') },
      { value: '1', label: t('common:active') },
      { value: '0', label: t('common:inactive') },
    ],
    [t]
  )

  const isDirty = formik.values.name !== currentName || formik.values.status !== currentStatus
  const canCancel = currentName === '' && currentStatus === ''

  const handleCancel = React.useCallback(() => {
    setSearchParams({
      name: '',
      status: '',
      page: DEFAULT_PAGE,
      perPage: DEFAULT_PER_PAGE,
    })
    formik.resetForm()
    formik.submitForm()
  }, [setSearchParams, formik])

  return (
    <fieldset disabled={formik.isSubmitting} className="flex w-full flex-col justify-between">
      <FormError />

      <div className="mb-3 flex w-full flex-wrap justify-between">
        <div className="mr-0 w-full min-w-[100px] md:!mb-0 md:mr-2 md:w-[49%]">
          <TextInput type="text" label={t('common:name')} name="name" placeholder={t('common:name')} />
        </div>

        <div className="w-full min-w-[100px] md:w-[49%]">
          <SelectInput
            label={t('common:status')}
            name="status"
            options={statusOptions}
            disabled={formik.isSubmitting}
          />
        </div>
      </div>

      <div className="flex w-full justify-end">
        <Button
          variant="red"
          disabled={formik.isSubmitting || !isDirty}
          type="submit"
          onClick={() => formik.submitForm()}
        >
          {t('common:search')}
        </Button>

        <Button
          variant="yellow"
          type="reset"
          disabled={formik.isSubmitting || canCancel}
          className="ml-2"
          onClick={handleCancel}
        >
          {t('common:cancel')}
        </Button>
      </div>
    </fieldset>
  )
}

interface TableContentProps {
  readonly searchParams: URLSearchParams
}

function TableContent({ searchParams }: TableContentProps): JSX.Element | null {
  const headers = useAuthenticatedHeaders()
  const t = useTranslatable()
  const locale = useLocale()

  const name = searchParams.get('name') || ''
  const status = searchParams.get('status') || ''
  const page = searchParams.get('page') || DEFAULT_PAGE
  const perPage = searchParams.get('perPage') || DEFAULT_PER_PAGE
  const sortBy = searchParams.get('sortBy') || ''

  const { data: faculties } = useApi({
    endpoint: Api.getAdministratorFaculties,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          page: parseInt(page, 10),
          perPage: parseInt(perPage, 10),
          'filters[name]': name || '',
          'filters[status]': status || '',
          sortBy,
        },
      }),
      [headers, page, perPage, name, status, sortBy]
    ),
  })

  if (!faculties?.data || faculties.data.length === 0) {
    return <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
  }

  return (
    <div className="mb-7">
      <PaginationTop pagination={faculties.meta.pagination} />

      <Table.Table className="mt-7">
        <Table.Thead>
          <TableHeaderRow t={t} />
        </Table.Thead>

        <Table.Tbody>
          {faculties.data.map((faculty) => (
            <TableRow key={faculty.id} faculty={faculty} locale={locale} t={t} />
          ))}
        </Table.Tbody>
      </Table.Table>

      <PaginationBottom pagination={faculties.meta.pagination} />
    </div>
  )
}

interface TableHeaderRowProps {
  readonly t: ReturnType<typeof useTranslatable>
}

function TableHeaderRow({ t }: TableHeaderRowProps): JSX.Element {
  return (
    <Table.Tr>
      <Table.Th className="!pl-6">
        <div className="flex items-center gap-2">
          <span>{t('common:unique')}</span>
          <SortButtons sortField="id" />
        </div>
      </Table.Th>

      <Table.Th>
        <div className="flex items-center gap-2">
          <span>{t('common:code')}</span>
          <SortButtons sortField="code" />
        </div>
      </Table.Th>

      <Table.Th>
        <div className="flex items-center gap-2">
          <span>{t('common:status')}</span>
          <SortButtons sortField="status" />
        </div>
      </Table.Th>

      <Table.Th>
        <div className="flex items-center gap-2">
          <span>{t('common:name')}</span>
          <SortButtons sortField="name" />
        </div>
      </Table.Th>
    </Table.Tr>
  )
}

interface TableRowProps {
  readonly faculty: Api.AdministratorFaculty
  readonly locale: string
  readonly t: ReturnType<typeof useTranslatable>
}

function TableRow({ faculty, locale, t }: TableRowProps): JSX.Element {
  const isActive = faculty.status.value === 1
  const statusText = isActive ? t('common:active') : t('common:inactive')
  const statusClassName = isActive ? 'text-green-600' : 'text-gray-500'

  return (
    <tr>
      <Table.Td className="whitespace-nowrap">{faculty.id}</Table.Td>

      <Table.Td>{faculty.code}</Table.Td>

      <Table.Td>
        <span className={statusClassName}>{statusText}</span>
      </Table.Td>

      <Table.Td>
        <Link
          to={`/${locale}/administrator/administration/faculties/${faculty.id}`}
          className="text-primaryBlueLink hover:underline"
        >
          {faculty.name}
        </Link>
      </Table.Td>
    </tr>
  )
}
